// Drift Team Website 2025
// src/contexts/AuthContext.jsx
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 18.04.2025 to add refresh token support and improve local storage handling

import { createContext, useState, useEffect } from 'react';
import { login, logout, checkAuth } from '../api/auth';
import { useNavigate } from 'react-router-dom';
import { TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY, AUTH_STORAGE_KEY } from '../config';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const userData = await checkAuth();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Authentication check failed:', error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  const loginFn = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const userData = await login(email, password);
      setCurrentUser(userData);
      return userData;
    } catch (error) {
      setError(error.message || 'Failed to login');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logoutFn = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setCurrentUser(null);
      setLoading(false);
      navigate('/login', { replace: true });
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login: loginFn,
    logout: logoutFn,
    isAuthenticated: !!currentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};